import { Box, Tooltip, makeStyles } from "@material-ui/core";
import BuildIcon from "@material-ui/icons/Build";
import { getCalendarEntryViewKey } from "Components/Calendar/CalendarEntryHelper";
import CalendarGroupRows from "Components/Calendar/CalendarGroupRows";
import {
	CellSize,
	getSplittedScheduleGroupIds,
	useIsCalendarSubHeadOpen,
} from "Components/Calendar/CalendarHelper";
import CalendarMemberRows from "Components/Calendar/CalendarMemberRows";
import CalendarNameColumn from "Components/Calendar/CalendarNameColumn";
import CalendarResourceRows from "Components/Calendar/CalendarResourceRows";
import CalenderRootView from "Components/Calendar/CalendarRootView";
import CalendarRow from "Components/Calendar/CalendarRow";
import CalendarSchedulePhaseRows, {
	CalendarSchedulePhaseRow,
} from "Components/Calendar/CalendarSchedulePhaseRows";
import { verifyError } from "Components/Common/helpers/verifyError";
import { useCalendar } from "Components/Common/hooks/useCalendar";
import {
	useEnabledWorkingDaysLength,
	useGetEnabledDaysDiff,
} from "Components/CompanySetting/CompanySettingHelper";
import { useCompanySetting } from "Components/CompanySetting/hooks/useCompanySetting";
import {
	CalendarEntryUpdate,
	getDropAreaViewKey,
	getMonthSchedulePhaseDropAreas,
} from "Components/DropArea/DropAreaHelper";
import {
	ValidMember,
	displayEmployeeName,
} from "Components/Members/MembersHelper";
import { useIsInternalAdmin } from "Components/Members/hooks/useIsInternalAdmin";
import { CalendarType } from "Components/Navigation/NavigationHelper";
import { getIsMultiWeekEnabled } from "Components/ProfileLimit/helpers/getIsMultiWeekEnabled";
import { useProfileLimits } from "Components/ProfileLimit/hooks/useProfileLimits";
import { useProjects } from "Components/Project/hooks/useProjects";
import {
	getScheduleEntryResizePixelSet,
	getSchedulePhaseResizePixelSet,
	useGetMonthBlockingResizePixel,
	useGetMonthScheduleEntriesResizePixelSet,
} from "Components/Resize/ResizeHelper";
import { useSchedule } from "Components/Schedule/hooks/useSchedule";
import { useScheduleEntries } from "Components/ScheduleEntry/hooks/useScheduleEntries";
import { useScheduleEntryModal } from "Components/ScheduleEntry/hooks/useScheduleEntryModal";
import { useScheduleEntryTypes } from "Components/ScheduleEntryType/hooks/useScheduleEntryTypes";
import { getPartialSchedulePhaseFromCalendarEntry } from "Components/SchedulePhase/SchedulePhaseHelper";
import { useSchedulePhaseModal } from "Components/SchedulePhase/hooks/useSchedulePhaseModal";
import { useSchedulePhases } from "Components/SchedulePhase/hooks/useSchedulePhases";
import { useScheduleResourceModal } from "Components/ScheduleResource/hooks/useScheduleResourceModal";
import MultiWeekDisabledView from "Components/ProfileLimit/views/MultiWeekDisabledView";
import { getFirstPaidDay } from "Components/ProfileLimit/helpers/getFirstPaidDay";
import { useTutorialModal } from "Components/Tutorial/hooks/useTutorialModal";
import { color } from "Configs/theme";
import {
	FALLBACK_DROP_AREA_COLOR_HEX,
	getColorHex,
	getColorHexFromDropArea,
} from "Helpers/ColorHelper";
import { getCalendarEntryViewTooltipText } from "Helpers/TooltipHelper";
import ErrorView from "Views/ErrorView";
import LoadingIndicator from "Views/LoadingIndicator";
import {
	Project,
	ScheduleEntryType,
	ScheduleEntryTypeCategory,
	ScheduleResource,
	Subscription,
} from "domain-entities";
import { useSnackbar } from "material-ui-snackbar-provider";
import moment, { Moment } from "moment";
import React, { FC, Fragment } from "react";
import { v4 } from "uuid";
import CalendarMonthDropAreaView from "./CalendarMonthDropAreaView";
import CalendarMonthEntryView from "./CalendarMonthEntryView";
import {
	MergedCalendarEntry,
	getMonthMergedCalendarEntryColorHex,
	getMonthScheduleEntries,
	getMonthSchedulePhasesResizePixelSet,
	useGetMonthRowItems,
} from "./CalendarMonthHelper";
import { DropArea } from "Components/DropArea/types/DropArea";

interface Props {
	subscription?: Subscription;
	cellSize: CellSize;
}

const CalendarMonthView: FC<Props> = (props) => {
	const snackbar = useSnackbar();
	const isInternalAdmin = useIsInternalAdmin();
	const companySettingStatuses = useCompanySetting(
		(state) => state.item?.status
	);
	const projects = useProjects((state) => state.projects);
	const profileLimits = useProfileLimits((state) => state.items);
	const scheduleEntries = useScheduleEntries((state) => state.items);
	const enabledWorkingDaysLength = useEnabledWorkingDaysLength();
	const getMonthRowItems = useGetMonthRowItems();
	const getMonthScheduleEntriesResizePixelSet =
		useGetMonthScheduleEntriesResizePixelSet();
	const getMonthBlockingResizePixel = useGetMonthBlockingResizePixel();
	const getEnabledDaysDiff = useGetEnabledDaysDiff();
	const firstPaidDay = getFirstPaidDay();
	const scheduleEntryTypes = useScheduleEntryTypes((state) => state.items);
	const isInitializingScheduleEntryTypes = useScheduleEntryTypes(
		(state) => state.isInitializing
	);

	const scheduleEntryTypesError = useScheduleEntryTypes((state) => state.error);
	const startOfCalendar = useCalendar((state) => state.startOfWeek);
	const classes = useStyles({
		startOfCalendar,
		nameWidth: props.cellSize.month.nameWidth,
		dayWidth: props.cellSize.month.dayWidth,
		enabledWorkingDaysLength,
	});
	const scheduleGroups = useSchedule((state) => state.item?.groups);
	const { isCalendarSubHeadOpen, setIsCalendarSubHeadOpen } =
		useIsCalendarSubHeadOpen();
	const endOfCalendar = startOfCalendar
		.clone()
		.add(props.cellSize.month.weeksLength - 1, "weeks")
		.add(enabledWorkingDaysLength, "days");

	const schedulePhasesResizePixelSet = getMonthSchedulePhasesResizePixelSet(
		startOfCalendar,
		props.cellSize,
		enabledWorkingDaysLength
	);

	const scheduleEntriesResizePixelSet = getMonthScheduleEntriesResizePixelSet(
		startOfCalendar,
		endOfCalendar,
		props.cellSize
	);

	const monthScheduleEntries = getMonthScheduleEntries(
		scheduleEntries,
		startOfCalendar,
		endOfCalendar
	);

	const { scheduleGroupMemberIds, scheduleGroupResourceIds } =
		getSplittedScheduleGroupIds(scheduleGroups);

	function renderSchedulePhaseRow(row: CalendarSchedulePhaseRow) {
		const colorHex = getColorHex(row.project.colorTag);
		const dropAreas = isInternalAdmin
			? getMonthSchedulePhaseDropAreas(
					row.project.id,
					row.items.map((item) => item.schedulePhase),
					startOfCalendar,
					props.cellSize.month.weeksLength,
					enabledWorkingDaysLength
			  )
			: [];

		return (
			<Box className={classes.daysColumn}>
				{row.items.map((rowItem) => {
					const schedulePhaseResizePixelSet = getSchedulePhaseResizePixelSet(
						schedulePhasesResizePixelSet,
						row.items.map((item) => item.schedulePhase),
						rowItem.schedulePhase
					);

					return (
						<CalendarMonthEntryView
							key={rowItem.calendarEntry.id}
							colorHex={colorHex}
							calendarEntry={rowItem.calendarEntry}
							calendarEntries={[]}
							projects={projects}
							cellSize={props.cellSize}
							startOfCalendar={startOfCalendar}
							endOfCalendar={endOfCalendar}
							resizePixelSet={schedulePhaseResizePixelSet}
							dropType="project"
							isFullDay
							selectCalendarEntry={() => {
								useSchedulePhaseModal.getState().openWithItem({
									project: row.project,
									schedulePhase: rowItem.schedulePhase,
								});
							}}
							tooltip={getCalendarEntryViewTooltipText({
								projectName: projects.find(
									(project) => project.id === rowItem.schedulePhase.projectId
								)?.name,
								phaseStatusName:
									companySettingStatuses?.find(
										(status) => status.id === rowItem.schedulePhase.statusId
									)?.name || "-",
							})}
							onResizeCalendarEntry={async (calendarEntry) => {
								try {
									await useSchedulePhases
										.getState()
										.updateItem(
											getPartialSchedulePhaseFromCalendarEntry(calendarEntry)
										);
								} catch (error) {
									snackbar.showMessage(verifyError(error).message);
								}
							}}
						/>
					);
				})}
				{dropAreas.map((dropArea, index) => (
					<Fragment key={index}>
						{renderSchedulePhaseDropArea(dropArea, colorHex, row.project)}
					</Fragment>
				))}
			</Box>
		);
	}

	function renderSchedulePhaseDropArea(
		dropArea: DropArea,
		colorHex: string,
		project?: Project
	) {
		return (
			<CalendarMonthDropAreaView
				dropArea={dropArea}
				cellSize={props.cellSize}
				startOfCalendar={startOfCalendar}
				colorHex={colorHex}
				acceptedDropTypes={[]}
				onClick={() =>
					useSchedulePhaseModal.getState().openWithItem(
						{
							project,
							forSchedulePhase: {
								start: dropArea.start,
								end: dropArea.end,
							},
						},
						true
					)
				}
			/>
		);
	}

	function renderEmptySchedulePhaseRow() {
		const weeks = Array(props.cellSize.month.weeksLength).fill(null);
		const dropAreas = weeks.map((_, index) => {
			const startOfWeek = startOfCalendar.clone().add(index, "weeks");

			return (
				<Fragment key={index}>
					{renderSchedulePhaseDropArea(
						{
							start: startOfWeek,
							end: startOfWeek
								.clone()
								.add(enabledWorkingDaysLength - 1, "days"),
						},
						FALLBACK_DROP_AREA_COLOR_HEX
					)}
				</Fragment>
			);
		});

		return (
			<Box className={classes.daysColumn}>
				{isInternalAdmin ? dropAreas : null}
			</Box>
		);
	}

	function renderScheduleEntriesColumn(
		mergedCalendarEntries: MergedCalendarEntry[],
		dropAreas: DropArea[],
		scheduleEntryTypes: ScheduleEntryType[],
		calendarEntryUpdate: CalendarEntryUpdate,
		isMultiWeekEnabled?: boolean
	) {
		const enabledDaysDiff = getEnabledDaysDiff(startOfCalendar, firstPaidDay);

		return (
			<Box className={classes.daysColumn}>
				{mergedCalendarEntries.map((mergedCalendarEntry) => {
					const { colorHex, colorHexes } = getMonthMergedCalendarEntryColorHex(
						mergedCalendarEntry,
						projects,
						scheduleEntryTypes
					);

					const blockingResizePixel = getMonthBlockingResizePixel(
						mergedCalendarEntry,
						mergedCalendarEntries
					);

					const resizePixelSet = getScheduleEntryResizePixelSet(
						scheduleEntriesResizePixelSet,
						blockingResizePixel
					);

					return (
						<CalendarMonthEntryView
							key={getCalendarEntryViewKey(mergedCalendarEntry.calendarEntry)}
							calendarEntry={mergedCalendarEntry.calendarEntry}
							calendarEntries={mergedCalendarEntry.calendarEntries}
							onResizeCalendarEntry={async (scheduleEntry) => {
								try {
									await useScheduleEntries.getState().updateItem(scheduleEntry);
								} catch (error) {
									snackbar.showMessage(verifyError(error).message);
								}
							}}
							colorHex={colorHex}
							colorHexes={colorHexes}
							projects={projects}
							cellSize={props.cellSize}
							startOfCalendar={startOfCalendar}
							endOfCalendar={endOfCalendar}
							selectCalendarEntry={() => {
								if (mergedCalendarEntry.calendarEntries.length === 1) {
									useScheduleEntryModal
										.getState()
										.openWithItem(mergedCalendarEntry.calendarEntries[0]);
								} else {
									const weeksDiff = moment
										.unix(mergedCalendarEntry.calendarEntry.startTime)
										.startOf("week")
										.diff(moment().startOf("week"), "weeks");

									useCalendar.getState().setWeekPlus(weeksDiff);
									useCalendar.getState().setCalendarType(CalendarType.WEEK);
								}
							}}
							dropType={mergedCalendarEntry.calendarEntry.typeId || "project"}
							resizePixelSet={resizePixelSet}
							tooltip={getCalendarEntryViewTooltipText({
								projectName: projects.find(
									(project) =>
										project.id === mergedCalendarEntry.calendarEntry.projectId
								)?.name,
								entryTypeName: scheduleEntryTypes.find(
									({ id }) => id === mergedCalendarEntry.calendarEntry.typeId
								)?.name,
							})}
						/>
					);
				})}
				{dropAreas.map((dropArea) => {
					const colorHex = getColorHexFromDropArea(
						dropArea,
						scheduleEntryTypes
					);

					const acceptedDropTypes = dropArea.isPlaceholder
						? scheduleEntryTypes.map((item) => item.id)
						: ["project"];

					return (
						<CalendarMonthDropAreaView
							key={`${getDropAreaViewKey(dropArea, acceptedDropTypes)}`}
							startOfCalendar={startOfCalendar}
							dropArea={dropArea}
							cellSize={props.cellSize}
							colorHex={colorHex}
							acceptedDropTypes={acceptedDropTypes}
							calendarEntryUpdate={calendarEntryUpdate}
							updateScheduleEntry={async (calendarEntry) => {
								try {
									await useScheduleEntries.getState().updateItem({
										...calendarEntry,
										...calendarEntryUpdate,
										startTime: dropArea.start.unix(),
										endTime: dropArea.end.unix(),
									});
								} catch (error) {
									snackbar.showMessage(verifyError(error).message);
								}
							}}
							addScheduleEntry={async (calendarEntry) => {
								try {
									await useScheduleEntries.getState().setItem({
										...calendarEntry,
										...calendarEntryUpdate,
										id: v4(),
										startTime: dropArea.start.unix(),
										endTime: dropArea.end.unix(),
									});

									setTimeout(() => {
										useTutorialModal.getState().openWithItem("resize");
									}, 200);
								} catch (error) {
									snackbar.showMessage(verifyError(error).message);
								}
							}}
							onClick={() => {
								useScheduleEntryModal.getState().openWithItem(
									{
										id: v4(),
										typeId: dropArea.typeId,
										startTime: dropArea.start.unix(),
										endTime: dropArea.end.unix(),
										...calendarEntryUpdate,
									},
									true
								);
							}}
						/>
					);
				})}
				{!isMultiWeekEnabled && (
					<MultiWeekDisabledView
						left={
							props.cellSize.month.dayWidth *
								getEnabledDaysDiff(startOfCalendar, firstPaidDay) +
							(enabledDaysDiff ? 1 : 2)
						}
					/>
				)}
			</Box>
		);
	}

	function renderMemberRow(member: ValidMember) {
		const filteredScheduleEntryTypes = scheduleEntryTypes.filter(
			(item) => item.category === ScheduleEntryTypeCategory.USER
		);

		const calendarEntries = monthScheduleEntries.filter(
			(item) => item.userId === member.id
		);

		const { mergedCalendarEntries, dropAreas } = getMonthRowItems(
			calendarEntries,
			startOfCalendar,
			endOfCalendar
		);

		const calendarEntryUpdate: CalendarEntryUpdate = {
			userId: member.id,
			resourceId: undefined,
		};

		const isMultiWeekEnabled = getIsMultiWeekEnabled(member.id, profileLimits);
		const employeeName = displayEmployeeName(member);

		return (
			<CalendarRow
				key={member.id}
				renderColumns={() => (
					<Fragment>
						<CalendarNameColumn width={props.cellSize.month.nameWidth}>
							<Tooltip title={employeeName || ""} placement="top">
								<div>{employeeName}</div>
							</Tooltip>
						</CalendarNameColumn>
						{renderScheduleEntriesColumn(
							mergedCalendarEntries,
							dropAreas,
							filteredScheduleEntryTypes,
							calendarEntryUpdate,
							isMultiWeekEnabled
						)}
					</Fragment>
				)}
			/>
		);
	}

	function renderResourceRow(resource: ScheduleResource) {
		const filteredScheduleEntryTypes = scheduleEntryTypes.filter(
			(item) => item.category === ScheduleEntryTypeCategory.RESOURCE
		);

		const calendarEntries = monthScheduleEntries.filter(
			(item) => item.resourceId === resource.id
		);

		const { mergedCalendarEntries, dropAreas } = getMonthRowItems(
			calendarEntries,
			startOfCalendar,
			endOfCalendar
		);

		const calendarEntryUpdate: CalendarEntryUpdate = {
			resourceId: resource.id,
			userId: undefined,
		};

		return (
			<CalendarRow
				key={resource.id}
				renderColumns={() => (
					<Fragment>
						<CalendarNameColumn
							width={props.cellSize.month.nameWidth}
							isEditable={isInternalAdmin}
							onClickEdit={() =>
								useScheduleResourceModal.getState().openWithItem(resource)
							}
						>
							<Tooltip title={resource.name || ""} placement="top">
								<div>
									<BuildIcon />
									{resource.name}
								</div>
							</Tooltip>
						</CalendarNameColumn>
						{renderScheduleEntriesColumn(
							mergedCalendarEntries,
							dropAreas,
							filteredScheduleEntryTypes,
							calendarEntryUpdate,
							true,
						)}
					</Fragment>
				)}
			/>
		);
	}

	function renderRows() {
		if (isInitializingScheduleEntryTypes) return <LoadingIndicator />;
		if (scheduleEntryTypesError) {
			return <ErrorView error={scheduleEntryTypesError} />;
		}
		return (
			<Fragment>
				<CalendarSchedulePhaseRows
					startOfCalendar={startOfCalendar}
					endOfCalendar={endOfCalendar}
					nameWidth={props.cellSize.month.nameWidth}
					isCalendarSubHeadOpen={isCalendarSubHeadOpen}
					setIsCalendarSubHeadOpen={setIsCalendarSubHeadOpen}
					renderEmptyRow={renderEmptySchedulePhaseRow}
					renderRow={renderSchedulePhaseRow}
				/>
				<CalendarGroupRows
					isCalendarSubHeadOpen={isCalendarSubHeadOpen}
					setIsCalendarSubHeadOpen={setIsCalendarSubHeadOpen}
					renderMemberRow={renderMemberRow}
					renderResourceRow={renderResourceRow}
				/>
				<CalendarMemberRows
					scheduleGroupMemberIds={scheduleGroupMemberIds}
					isCalendarSubHeadOpen={isCalendarSubHeadOpen}
					setIsCalendarSubHeadOpen={setIsCalendarSubHeadOpen}
					renderMemberRow={renderMemberRow}
				/>
				<CalendarResourceRows
					scheduleGroupResourceIds={scheduleGroupResourceIds}
					isCalendarSubHeadOpen={isCalendarSubHeadOpen}
					setIsCalendarSubHeadOpen={setIsCalendarSubHeadOpen}
					renderResourceRow={renderResourceRow}
				/>
			</Fragment>
		);
	}

	return (
		<CalenderRootView cellSize={props.cellSize}>
			{renderRows()}
		</CalenderRootView>
	);
};

export default CalendarMonthView;
interface StyleProps {
	startOfCalendar: Moment;
	nameWidth: number;
	dayWidth: number;
	enabledWorkingDaysLength: number;
}

const useStyles = makeStyles({
	daysColumn: (props: StyleProps) => ({
		display: "flex",
		flex: 1,
		position: "relative",
		borderBottom: `1px solid ${color.tableCellBorder}`,
		borderLeft: `1px solid ${color.tableCellBorder}`,
		backgroundImage: getDaysColumnBackgroundImage(props),
		backgroundSize: `${props.dayWidth * props.enabledWorkingDaysLength}px 1px`,
	}),
	subscription: {
		display: "flex",
		position: "absolute",
		backgroundColor: color.totalBackground,
		zIndex: 2,
		top: 0,
		left: 0,
		height: "100%",
		width: window.innerWidth,
	},
});

function getLinearGradient(color: string, toRight: number) {
	return `linear-gradient(to right, ${color} ${toRight}px, transparent 1px)`;
}

function getDaysColumnBackgroundImage(props: StyleProps) {
	const days = Array(props.enabledWorkingDaysLength).fill(null);
	const gradients = days.reduce<string[]>((result, _, index) => {
		const isoWeekday = props.startOfCalendar
			.clone()
			.add(index, "days")
			.isoWeekday();
		const isWeekend = [6, 7].includes(isoWeekday);

		result.push(
			getLinearGradient(
				color.tableCellBorder,
				props.dayWidth * index + (index === 0 ? 2 : 1)
			)
		);

		result.push(
			getLinearGradient(
				isWeekend ? "#f9f9f9" : color.totalBackground,
				props.dayWidth * (index + 1) + (index === days.length - 1 ? -1 : 0)
			)
		);

		if (index === days.length - 1) {
			result.push(
				getLinearGradient(
					color.tableCellBorder,
					props.dayWidth * (index + 2) - 1
				)
			);
		}

		return result;
	}, []);

	return gradients.join(",");
}
